import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useStore } from "@/lib/store";
import { dtfRate, markupOf } from "@/lib/pricing";

const Row = ({ label, value, testid }) => (
  <div className="flex items-center justify-between border-b border-white/10 py-3 last:border-0" data-testid={testid}>
    <span className="text-slate-300">{label}</span>
    <span className="font-mono-num font-bold text-gold">{value}</span>
  </div>
);

export const Pricing = () => {
  const { t, money, num } = useLang();
  const { settings: s } = useStore();
  if (!s) return <section id="pricing" className="min-h-[400px]" />;
  const tiers = [...s.dtf_tiers].sort((a, b) => a.min_area - b.min_area);
  const rate = (r) => money(r * markupOf(s), 3);
  return (
    <section id="pricing" className="scroll-mt-20 mx-auto max-w-7xl px-5 py-20 lg:px-8" data-testid="pricing-section">
      <div className="max-w-2xl">
        <h2 className="font-display text-3xl sm:text-4xl">{t("pricing.title")}</h2>
        <p className="mt-3 text-slate-400 md:text-lg">{t("pricing.sub")}</p>
      </div>
      <div className="mt-12 grid gap-6 lg:grid-cols-[1.3fr_1fr]">
        <div className="panel p-7">
          <div className="flex items-center justify-between text-xs font-extrabold uppercase tracking-wider text-slate-400">
            <span>{t("pricing.dtf_head")}</span>
            <span>{t("pricing.rate_head")}</span>
          </div>
          <div className="mt-3">
            {tiers.map((tier, i) => (
              <Row key={i} testid={`pricing-tier-${i}`}
                label={i === 0 ? t("pricing.upto", { n: num(tiers[1]?.min_area ?? 0, 0) }) : t("pricing.over", { n: num(tier.min_area, 0) })}
                value={rate(tier.rate)} />
            ))}
          </div>
          <p className="mt-4 text-sm text-slate-400">{t("pricing.example", { rate: rate(dtfRate(800, tiers)) })}</p>
          <div className="mt-6 rounded-md bg-ink/60 p-4">
            <div className="flex items-center justify-between">
              <span className="font-extrabold">{t("pricing.uv_head")}</span>
              <span className="font-mono-num font-bold text-gold" data-testid="pricing-uv-rate">{t("pricing.uv_rate", { rate: rate(s.uv_rate) })}</span>
            </div>
            <p className="mt-1 text-sm text-slate-400">{t("pricing.uv_note")}</p>
          </div>
        </div>
        <div className="flex flex-col gap-6">
          <div className="panel p-7">
            <Row label={t("pricing.setup")} value={money(s.profit_per_line)} testid="pricing-setup" />
            <p className="mt-5 text-xs font-extrabold uppercase tracking-wider text-slate-400">{t("pricing.extras")}</p>
            <Row label={t("pricing.rush")} value={t("pricing.rush_v", { n: num(s.rush_multiplier) })} testid="pricing-rush" />
            <Row label={t("pricing.cut")} value={t("pricing.cut_v", { fee: money(s.cut_fee) })} testid="pricing-cut" />
            <Row label={t("pricing.check")} value={t("pricing.check_v", { fee: money(s.file_check_fee) })} testid="pricing-check" />
          </div>
          <div className="panel border-gold/30 bg-gradient-to-br from-panel to-panel2 p-7">
            <p className="text-xs font-extrabold uppercase tracking-wider text-slate-400">{t("pricing.ship")}</p>
            <p className="mt-2 text-lg font-extrabold" data-testid="pricing-shipping">
              {t("pricing.ship_v", { flat: money(s.shipping_flat), free: money(s.free_shipping_over, 0) })}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export const FinalCta = () => {
  const { t } = useLang();
  return (
    <section className="grain relative overflow-hidden bg-ruby" data-testid="final-cta">
      <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-fire/60 blur-3xl" />
      <div className="relative mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-6 px-5 py-14 lg:px-8">
        <div>
          <h2 className="font-display text-3xl sm:text-4xl">{t("cta.title")}</h2>
          <p className="mt-2 text-white/85 md:text-lg">{t("cta.sub")}</p>
        </div>
        <Link to="/order" className="btn-gold" data-testid="final-cta-btn">
          {t("cta.btn")} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </section>
  );
};
